import React from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useNavigate, useLocation, useParams } from "react-router-dom"

import * as PartnerApi from "features/Partner/api/PartnerApi"
import * as UserApi from "features/api/UserApi"

import ProtectedLayout from "layouts/Protected"
import { privateRoutes, publicRoutes } from "router/routes"

import { useAuth, useAlert } from "hooks"
import { isDefined } from "utils/validation"
import { jsonHash } from "utils/hash"
import { sortByProperty, openInNewTab } from "utils/functions"
import CaretIcon from "components/Elements/Icons/Caret"
import { isNotEmpty } from "utils/validation"
import {
    OCODE_GENDERS_LIST,
    ADMIN_BICYCODE,
    ADMIN_MFC,
    VELOCISTE_GENDER,
    ADMIN_MOUSTACHE
} from "utils/constants"
import { http } from "services/http"

import { prepareDataToPut } from "./hooks/prepareDataToPut"
import { fetchBOPartner } from "./hooks/fetchBOPartner"

// Partners components
import {
    ReferenceForm,
    OperatorsForm,
    StructureForm,
    ServicesForm,
    AddressForm,
    ManagerForm,
    WalletForm,
    BilingForm,
    MandateForm,
    BikeCollectionImportForm
} from "./Partials"

const defaultFormEvents = {
    ready: true,
    editable: false
}

// const PERMISSION_STORE = 'MANAGEBIKES'

export const PartnerPage = () => {
    const [partner, setPartner] = React.useState(null)
    const [updatedPartner, setUpdatedPartner] = React.useState(null)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const [isMFCSelected, setIsMFCSelected] = React.useState(false)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)
    const [jsonHashEntityData, setJsonHashEntityData] = React.useState({})
    const [jsonHashOperatorData, setJsonHashOperatorData] = React.useState({})

    const { alert } = useAlert()
    const { t } = useTranslation()
    const { user, logout } = useAuth()
    const { pathname } = useLocation()
    const { slug } = useParams()
	const navigate = useNavigate()

    const isPartnerAlreadyRegistered = isDefined(slug)
    const isProductionEnvironment = process.env.REACT_APP_MODE === "production" // Ne pas afficher le bloc Mandat en production sans le raccordement avec sage

    const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)
    const isAdminBicycode = user?.gender === ADMIN_BICYCODE
    const isAdminMFC = user?.gender === ADMIN_MFC
    const isAdminMoustache = user?.gender === ADMIN_MOUSTACHE

    const fetchData = async () => {
        try {
            const fetchedPartner = await fetchBOPartner(slug, isAdminOCode, setJsonHashEntityData, setJsonHashOperatorData)
            setPartner(fetchedPartner)
        } catch (error) {
            console.error(error)
            const erroMsg = error?.message?.toString() || t("global_fetch_error", { ns: "partner" })
            if (erroMsg) alert("error", erroMsg)

            if (["InvalidArgument", "EntityStatusException", "InternalServer"].includes(error?.code)) navigate(-1)
            if (["NotAuthorizedException"].includes(error?.code)) logout()
        }
    }

    React.useEffect(() => {
		if(!isNotEmpty(partner)) fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

    const createPartner = async () => {
        try {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents({
                ...formEvents,
                ready: false
            })

            const newPartner = prepareDataToPut({
                partner: updatedPartner,
                isAdminMFC,
                isAdminBicycode,
                isAdminMoustache
            })

            const { getError } = await PartnerApi.createPartner(newPartner)
            if (getError()) throw getError()

            alert("success", t("edit_success", { ns: "partner" }))
            navigate(privateRoutes.validatedPartner.path)

        } catch (error) {
            console.error(error)
            const erroMsg = error?.message?.toString()
            if (erroMsg) alert("error", erroMsg)

            if (["Access token is required."].includes(error?.error?.message)) logout()

        } finally {
            setFormEvents(s => ({
                ...s,
                ready: true
            }))
        }
    }

    const editPartner = async (updatedPartner) => {
        try {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents(s => ({
                ...s,
                ready: false
            }))

            const newPartner = prepareDataToPut({
                isUpdate: true,
                partner: updatedPartner,
                isAdminMFC,
                isAdminBicycode,
                isAdminMoustache
            })

            let updateEntityData = true
            if (jsonHash(newPartner.entity_data) === jsonHashEntityData) {
                updateEntityData = false
            }
            const data_operators = JSON.parse(JSON.stringify(newPartner.data_operators))
            let updateOperator = true
            if (jsonHash(data_operators.sort(sortByProperty("operator"))) === jsonHashOperatorData) {
                updateOperator = false
            }

            if (!updateEntityData && !updateOperator) {
                alert("error", t("edit_error_TooManyRequestsException", { ns: "api" }))
                return
            } else {
                if (updateEntityData) {
                    delete newPartner.data_operators
                    const { getError } = await PartnerApi.updatePartnerAccount(newPartner)
                    if (getError()) throw getError()
                }
                if (updateOperator) {
                    const { res: updatedPartner } = await PartnerApi.getPartner(newPartner.entity_id)
                    const newOperator = {
                        entity_id: newPartner.entity_id,
                        data_operators: data_operators,
                        last_row_hash: updatedPartner.row_hash
                    }

                    const { getError } = await PartnerApi.updatePartnerOperator(newOperator)
                    if (getError()) throw getError()
                }
                alert("success", t("edit_success", { ns: "partner" }))
            }
        } catch (error) {
            console.error(error)
            const erroMsg = error?.message?.toString()
            if (erroMsg) alert("error", erroMsg)
        } finally {
            await fetchData()
            setFormEvents(s => ({
                ...s,
                ready: true,
                editable: false
            }))
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
            alert("error", t("global.form_error"))
            // TODO: inner scroll inside component when error
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            })
        }
        else {
            await createPartner()
        }
    }

    // ADMIN SPECIFIC FEAT.
    // ----------------------
    const connectAsPartner = async () => {
        const partnerId = partner.entity_id
        try {
            // Get backend access token (token to access platform as backend)
            // (token should not be automatically stored by Axios)
            const { res: connectedPartner, getError } = await UserApi.signInAsSupport(partnerId)
            if (getError()) throw getError()

            const backendAccessToken = connectedPartner?.backend_access_token
            if (!backendAccessToken) throw new Error("backend access token is missing")

            const isBackendAccessTokenExpired = http.isTokenExpired(backendAccessToken)
            if (isBackendAccessTokenExpired) throw new Error("backend access token is expired")

            const fullUrl = window.location.href
            const baseUrl = fullUrl.replace(pathname, "")

            openInNewTab(
                `${baseUrl}${publicRoutes.login.path}?backend_access_token=${backendAccessToken}`,
                { focus: true }
            )

        } catch (error) {
            // console.log("error: ", error)
            alert("error", t("support.fetch_error"))
        }
    }

    const componentCommonProps = {
        partner: partner,
        updatedPartner: updatedPartner,
        setUpdatedPartner: setUpdatedPartner,
        errors: errors,
        setErrors: setErrors,
        showLocalErrors: showLocalErrors,
        setShowLocalErrors: setShowLocalErrors
    }

    return (
        <ProtectedLayout
            className={
                "px-5 py-7 "
                + (partner?.status !== 0 ? "lg:py-7" : "")
            }
        >

            {!isNotEmpty(partner)
                ?
                <div className="flex justify-center items-center py-12">
                    <div className="c-spinner"></div>
                </div>
                :
                <div className="l-container">
                    <div className={"c-section-title mb-7 " + (partner.status === 0 ? "mt-7" : "")}>
                        <div className="c-section-title__content justify-between">
                            <div className="flex justify-between w-full">
                                <div className="flex items-center justify-start">
                                    <button onClick={() => navigate(-1)} className="flex" aria-label={t("global.back")}>
                                        <span className="flex py-2" style={{ transform: "rotate(180deg)" }}><CaretIcon size={30} /></span>
                                    </button>
                                    <h1 className="c-section-title__title c-h2 text-primary font-medium ml-5">
                                        {isPartnerAlreadyRegistered && (
                                            <>
                                                {partner.status === 1 && <>{t("title_edit", { ns: "partner" })} </>}
                                                {partner.raison_sociale}
                                            </>
                                        )}
                                        {!isPartnerAlreadyRegistered && t("title_add", { ns: "partner" })}
                                    </h1>
                                </div>

                                {// Button only available for ocode and BC admins (gender allowed on API side)
                                (isAdminOCode || isAdminBicycode) && isPartnerAlreadyRegistered && (
                                    <button
                                        className="c-btn c-btn--primary c-btn--s px-7 mx-5"
                                        onClick={connectAsPartner}
                                    >
                                        {t("support.connect_as")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    {partner.status === 1 &&
                        <div className="c-submenu px-7 pb-7">
                            <div className="flex items-center justify-start w-full">
                                <div className={"c-submenu__item px-5 " + (pathname === `/partenaire/${partner.entity_id}` ? "is-active" : "")}>
                                    <NavLink to={`/partenaire/${partner.entity_id}`} className="flex">
                                        {t("menu.store")}
                                    </NavLink>
                                </div>
                                <div className={"c-submenu__item px-5 " + (pathname === `/gestion-equipe/${partner.entity_id}` ? "is-active" : "")}>
                                    <NavLink
                                        to={{
                                            pathname: `/gestion-equipe-partenaire/${partner.entity_id}`,
                                            partnerProps: partner.raison_sociale
                                        }}
                                        className="flex">
                                        {t("menu.manage_team")}
                                    </NavLink>
                                </div>
                            </div>
                        </div>
                    }

                    {(isAdminBicycode || isAdminOCode) && isPartnerAlreadyRegistered &&
                        <ReferenceForm
                            {...componentCommonProps}
                            refresh={fetchData}
                            isEdit={isPartnerAlreadyRegistered}
                        />
                    }

                    {isAdminOCode &&
                        <OperatorsForm
                            {...componentCommonProps}
                            editCallback={editPartner}
                            isEdit={isPartnerAlreadyRegistered}
                            setIsMFCSelected={setIsMFCSelected}
                            isViewMode={(partner?.gender === VELOCISTE_GENDER) && isAdminBicycode} // A Bicycode Admin can't edit a bicycle partner, but view its information
                        />
                    }

                    <AddressForm
                        {...componentCommonProps}
                        editCallback={editPartner}
                        isEdit={isPartnerAlreadyRegistered}
                        isViewMode={(partner?.gender === VELOCISTE_GENDER) && isAdminBicycode}
                    />

                    <StructureForm
                        {...componentCommonProps}
                        editCallback={editPartner}
                        isEdit={isPartnerAlreadyRegistered}
                        isViewMode={(partner?.gender === VELOCISTE_GENDER) && isAdminBicycode}
                    />

                    {/*
                            We can see the block if :
                            - we select one of the operators on the list
                            - for Bicycode, if we select gender = 0 => velociste
                            - we are an admin
                        */}
                    {(isAdminMFC || partner.isMFC || isMFCSelected) &&

                        <ServicesForm
                            {...componentCommonProps}
                            editCallback={editPartner}
                            isEdit={isPartnerAlreadyRegistered}
                            isMFCSelected={isMFCSelected}
                        />
                    }

                    {(isAdminBicycode || isAdminOCode) && partner.isBicycode && isPartnerAlreadyRegistered &&
                        <>
                            <BilingForm
                                {...componentCommonProps}
                                editCallback={editPartner}
                                isEdit={true}
                                isViewMode={true}
                                isAdminEditing={isAdminBicycode || isAdminOCode}
                            />

                            {!isProductionEnvironment &&
                                <MandateForm
                                    {...componentCommonProps}
                                    isEdit={true}
                                    isViewMode={true}
                                    isAdminViewing={isAdminOCode || isAdminBicycode}
                                />
                            }
                        </>
                    }

                    <ManagerForm
                        {...componentCommonProps}
                        editCallback={editPartner}
                        refresh={fetchData}
                        isEdit={isPartnerAlreadyRegistered}
                        isViewMode={(partner?.gender === VELOCISTE_GENDER) && isAdminBicycode}
                    />

                    <WalletForm
                        {...componentCommonProps}
                        editCallback={editPartner}
                        isEdit={isPartnerAlreadyRegistered}
                        isViewMode={(partner?.gender === VELOCISTE_GENDER) && isAdminBicycode}
                    />

                    <BikeCollectionImportForm
                        {...componentCommonProps}
                        editCallback={editPartner}
                        isEdit={isPartnerAlreadyRegistered}
                        isViewMode={(partner?.gender === VELOCISTE_GENDER) && isAdminBicycode}
                    />

                    {!isPartnerAlreadyRegistered &&
                        <div className="flex justify-center mt-7 u-mg-negative-hz-m px-5 lg:mx-0 lg:px-0">
                            <button
                                type="submit"
                                className="c-btn c-btn--primary c-btn--m px-12 mx-5 uppercase"
                                onClick={handleSubmit}
                                disabled={!formEvents.ready}
                            >
                                {t("global.create")}
                            </button>
                        </div>
                    }
                </div>
            }
        </ProtectedLayout>
    )
}
